/**
 * Hook: GET /api/t/{slug}/jds/{jdId}/candidates?ranked=true&hide_completed=… —
 * ranked candidate list scoped to a JD (Phase 2.2).
 *
 * Surfaces the per-JD pipeline view's hero + compact card data. The shape
 * matches `RankingsListResponse` (`{items: RankedCandidateOut[]}`) — same
 * fields as the legacy `/rankings?jd_id=` endpoint, but JD-scoped server
 * side and with `hide_completed` filtering applied at the source (the
 * pre-pairings bridge: filters terminal-status candidates out of the
 * response before hand to avoid client-side races between `/candidates`
 * and `/rankings`).
 *
 * Cache key: `["jd-candidates", tenantSlug, jdId, {hideCompleted}]`.
 *
 * The Architect §3 query-keys list this distinctly from the legacy
 * `["rankings", slug, jdId]` so the two surfaces (Dashboard vs
 * Candidates table) can co-exist while we deprecate `/rankings`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import type { FitOverall } from "@/lib/fitScore";
import type { TenureSignal } from "./useRanking";

interface RankedJdCandidate {
  candidate_id: string;
  name: string;
  rank_position: number;
  summary: string;
  tenure_signal: TenureSignal;
  fit_overall: FitOverall;
  candidate_status: CandidateStatus;
  skills_tags: string[];
  /** ranking.system v3: stored 0–100 score; null for legacy rows. */
  fit_score: number | null;
}

interface RankedJdCandidatesResponse {
  items: RankedJdCandidate[];
  next_cursor: string | null;
}

interface UseRankedJdCandidatesOpts {
  jdId: string | undefined;
  hideCompleted?: boolean;
}

export function useRankedJdCandidates(
  opts: UseRankedJdCandidatesOpts,
): UseQueryResult<RankedJdCandidate[] | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { jdId, hideCompleted = false } = opts;

  return useQuery<RankedJdCandidate[] | null, ApiError>({
    queryKey: ["jd-candidates", tenantSlug, jdId, { hideCompleted }],
    queryFn: async () => {
      try {
        const r = await apiClient.get<RankedJdCandidatesResponse>(
          `/api/t/${tenantSlug}/jds/${jdId}/candidates`,
          {
            params: {
              ranked: "true",
              hide_completed: hideCompleted ? "true" : "false",
            },
          },
        );
        // Empty list = JD exists but no completed ranking_runs. Map to null
        // so the surface can render the pre-rank state (Dashboard hint
        // banner). Mirrors useRanking.ts:64 mapping.
        return r.data.items.length === 0 ? null : r.data.items;
      } catch (err) {
        // 404: JD not visible to this tenant. Surface as null data.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: !!tenantSlug && !!jdId,
    staleTime: 30_000,
  });
}
